import { Injectable } from '@angular/core';
import { Client, MakeOptional, dateToISOStringWithTimezone } from 'projects/common/src/public-api';
import { BusinessService } from './business.service';
import { ViewAsService } from './view-as.service';
import { LobbyService } from './lobby.service';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { PaymentService } from './payment.service';
import { UsersService } from './users.service';
import { TimeRange } from 'projects/common/src/lib/models/time-range.model';
import { rpcFilter, SupabaseService } from "./supabase.service";
import { map, shareReplay, switchMap, take } from "rxjs/operators";
import { Note } from "../models/note.model";
import { Assessment, AssessmentTileData } from "../models/assessment.model";
import { PaginationOptions } from "./pagination.service";
import { AssessmentClientRestore } from "projects/common/src/lib/models/client-transform";
import { UserProfile } from "projects/common/src/lib/models/user-profile.model";
import { RequestInvitationFormData } from "../modals/request-invitation/request-invitation.model";
import { NgxMaskService } from 'ngx-mask';

export interface AssessmentCreate {
    inOwnerId: number;
    inClient: MakeOptional<Client, 'salesTaxPercentage'>;
    inRanges: TimeRange[];
    inAssignee: number | null;
    inWorkflowId?: number;
    inJobType?: string;
    inNotes?: Note[];
}

export interface AssessmentRestore {
    inId: number;
    inCreatedAt: Date;
    inOwnerId: number;
    inClient: AssessmentClientRestore;
    inRanges: TimeRange[];
    inStatus: Assessment['status'];
    inDenied: boolean;
    inAssignee?: number;
    inWorkflowId?: number;
    inJobType?: string;
    inNotes?: Note[];
}

interface BaseAssessmentUpdate {
    inId: number;
    inOwnerId?: number | null;
    inStatus?: Assessment['status'];
    inClient?: MakeOptional<Client, 'salesTaxPercentage'>;
    inRanges?: TimeRange[];
    inJobType?: string;
}

interface AssessmentUpdateWithAssigneeData {
    inAssignee?: number | null;
    inDenied?: boolean;
}

export interface AssessmentEmail {
    schemaName?: string,
    itemId: number,
    timeRanges: { start: string, end: string }[],
    organizerName: string,
    businessEmail: string,
    businessName: string,
    clientName: string,
    businessPhoneNumber: string,
    clientEmailAddress: string | null,
    reschedule: boolean
}

export type AssessmentUpdate = BaseAssessmentUpdate | (BaseAssessmentUpdate & AssessmentUpdateWithAssigneeData);

@Injectable({
  providedIn: 'root'
})
export class AssessmentsService {

    constructor(
        protected businessService: BusinessService,
        protected viewAsService: ViewAsService,
        protected supabaseService: SupabaseService,
        protected paymentService: PaymentService,
        protected usersService: UsersService,
        protected lobbyService: LobbyService,
        protected snackbar: MatSnackBar,
        private maskService: NgxMaskService,
    ) {}

    assessmentObservable(id: number, pref = '') {
        return this.businessService.selectedBusiness$.pipe(
            switchMap(business => {
                return this.supabaseService.rpc<Assessment[]>({
                    cName: pref + '_assessment_' + id,
                    schema: business!.businessId,
                    fn: 'get_assessment_by_id',
                    tables: [
                        { table: 'assessment', filter: rpcFilter('id', 'eq', id) },
                        { table: 'assessment_client', filter: rpcFilter('assessment_id', 'eq', id) },
                        { table: 'assessment_range', filter: rpcFilter('assessment_id', 'eq', id) },
                        { table: 'workflow', filter: rpcFilter('assessment_id', 'eq', id) }
                    ],
                    options: { inId: id }
                },
                (changes, items) => {
                    if (!(changes.table === 'workflow' && changes.eventType === 'UPDATE'))
                        return null;
                    items[0].jobType = (changes.new as any).jobType;
                    return items;
                }).pipe(
                    map(assessments => {
                        if(!assessments || assessments.length === 0)
                            return null;
                        const assessment = assessments[0];
                        assessment.docType = 'Assessment';
                        return assessment;
                    })
                );
            }),
            switchMap(async assessment => {
                if (!assessment)
                    return null;
                const businessId = (await this.businessService.selectedBusiness$.pipe(take(1)).toPromise())!.businessId;
                const selectedUsers = await this.viewAsService.selectedUsersIds$.pipe(take(1)).toPromise();
                const canOpen = !selectedUsers ? true : await this.supabaseService.rpcFunc<boolean>(businessId, 'can_open_item', {
                    inItemType: 'assessment',
                    inItemId: id,
                    inUserIds: selectedUsers
                });
                return canOpen ? assessment : null;
            }),
            shareReplay({ bufferSize: 1, refCount: true }),
        );
    }

    assessmentsObservable(options: PaginationOptions, pref = '') {
        return this.businessService.selectedBusiness$.pipe(
            // TODO: This can be improved by adding the changes handler to handle each change separately and not to pull the list again.
            switchMap(business => {
                return this.supabaseService.rpc<AssessmentTileData[]>({
                    cName: pref + '_assessments',
                    schema: business!.businessId,
                    fn: 'get_assessments',
                    tables: [
                        { table: 'assessment' },
                        { table: 'assessment_client' },
                        { table: 'assessment_range' },
                        { table: 'workflow' }
                    ],
                    options
                },
                (changes, items) => {
                    if (!(changes.table === 'workflow' && changes.eventType === 'UPDATE'))
                        return null;
                    const data = changes.new as any;
                    const item = items.find(item => data.assessmentId === item.id);
                    if (item)
                        item.jobType = data.jobType;
                    return items;
                });
            }),
            map(assessments => {
                if(!assessments)
                    return [];
                for(const assessment of assessments)
                    assessment.docType = 'Assessment';
                return assessments;
            }),
            shareReplay({ bufferSize: 1, refCount: true }),
        );
    }

    async getAssessment(id: number) {
        const business = await this.businessService.selectedBusiness$.pipe(take(1)).toPromise();
        const assessment = (await this.supabaseService.rpcFunc<Assessment[]>(business!.businessId, 'get_assessment_by_id', { inId: id }))[0];
        assessment.docType = 'Assessment';
        return assessment;
    }

    async createAssessment(data: AssessmentCreate) {
        const business = (await this.businessService.selectedBusiness$.pipe(take(1)).toPromise())!;
        const users = await this.usersService.users$.pipe(take(1)).toPromise();
        const assessmentId = await this.supabaseService.rpcFunc<number>(business.businessId, 'create_assessment', data);
        const businessOwner = users!.find(user => user.role === 'owner')!;
        const organizerId = data.inAssignee ?? data.inOwnerId;
        const organizer = users!.find(user => user.id === organizerId)!;
        this.sendEmail({
            schemaName: business.businessId,
            itemId: assessmentId,
            timeRanges: data.inRanges.map(range => ({
                start: dateToISOStringWithTimezone(range.startTime),
                end: dateToISOStringWithTimezone(range.endTime),
            })),
            organizerName: organizer.firstName,
            businessEmail: businessOwner.email,
            businessName: business.businessName,
            clientName: data.inClient.firstName,
            businessPhoneNumber: this.applyPhoneNumberMask(business.phoneNumber),
            clientEmailAddress: data.inClient.email,
            reschedule: false
        });
        return assessmentId;
    }

    async updateAssessment(data: AssessmentUpdate) {
        const business = await this.businessService.selectedBusiness$.pipe(take(1)).toPromise();
        return this.supabaseService.rpcFunc(business!.businessId, 'update_assessment', data);
    }

    async restoreAssessment(data: AssessmentRestore) {
        const business = await this.businessService.selectedBusiness$.pipe(take(1)).toPromise();
        return this.supabaseService.rpcFunc(business!.businessId, 'restore_assessment', data);
    }

    async deleteAssessment(id: number, showUndoSnackbar?: boolean) {
        const business = await this.businessService.selectedBusiness$.pipe(take(1)).toPromise();
        return this.supabaseService.delete(business!.businessId, 'assessment', id);
    }

    async sendEmail(data: MakeOptional<AssessmentEmail, 'schemaName' | 'businessEmail' | 'businessName' | 'businessPhoneNumber'>) {
        if(!data.schemaName) {
            const business = (await this.businessService.selectedBusiness$.pipe(take(1)).toPromise())!;
            data.schemaName = business.businessId;
            data.businessName = business.businessName;
            data.businessPhoneNumber = this.applyPhoneNumberMask(business.phoneNumber);
        }
        if(!data.businessEmail) {
            const users = await this.usersService.users$.pipe(take(1)).toPromise();
            const owner = users!.find(user => user.role === 'owner')!;
            data.businessEmail = owner.email;
        }
        return this.supabaseService.edgeFunc('send-assessment-email', data);
    }

    async sendEmailForAssessment(assessment: Assessment, reschedule: boolean) {
        const business = (await this.businessService.selectedBusiness$.pipe(take(1)).toPromise())!;
        const users = await this.usersService.users$.pipe(take(1)).toPromise();
        const owner = users!.find(user => user.role === 'owner')!;
        const data = {
            schemaName: business.businessId,
            itemId: assessment.id,
            timeRanges: assessment.ranges.map(range => ({
                start: dateToISOStringWithTimezone(range.startTime),
                end: dateToISOStringWithTimezone(range.endTime),
            })),
            organizerName: assessment.assigneeFirstName ?? assessment.ownerFirstName,
            businessEmail: owner.email,
            businessName: business.businessName,
            clientName: assessment.firstName,
            businessPhoneNumber: this.applyPhoneNumberMask(business.phoneNumber),
            clientEmailAddress: assessment.email,
            reschedule
        }
        return this.sendEmail(data);
    }

    async sendEmailForFormData(formData: Required<RequestInvitationFormData>, original: Assessment, reschedule: boolean) {
        const business = (await this.businessService.selectedBusiness$.pipe(take(1)).toPromise())!;
        const users = await this.usersService.users$.pipe(take(1)).toPromise();
        const owner = users!.find(user => user.role === 'owner')!;

        const data = {
            schemaName: business.businessId,
            itemId: original.id,
            timeRanges: formData.ranges.map(range => ({
                start: dateToISOStringWithTimezone(range.startTime),
                end: dateToISOStringWithTimezone(range.endTime),
            })),
            organizerName: await this._getOrganizerName(formData, original, users!),
            businessEmail: owner.email,
            businessName: business.businessName,
            clientName: formData.client.firstName ?? original.firstName,
            businessPhoneNumber: this.applyPhoneNumberMask(business.phoneNumber),
            clientEmailAddress: formData.client.email ?? original.email,
            reschedule
        }
        return this.sendEmail(data);
    }

    private async _getOrganizerName(formData: Required<RequestInvitationFormData>, original: Assessment, users: UserProfile[]) {
        if(formData.assignees && formData.assignees[0])
            return users!.find(user => user.id === formData.assignees[0].id)!.firstName;
        if(original.assigneeFirstName)
            return original.assigneeFirstName;
        return users!.find(user => user.id === original.ownerId)!.firstName;
    }

    applyPhoneNumberMask(phoneNumber: string) {
        return this.maskService.applyMask(phoneNumber, '(000) 000-0000');
    }
}
